/**
 * safeReturnTo — guard the OIDC `returnTo` parameter.
 *
 * The ZitadelProvider calls `signinRedirect({ state: returnTo })`
 * where `returnTo` is whatever the calling page put into the URL.
 * A malicious actor could craft
 *   <a href="/login?returnTo=https://evil.example.com/phish">
 * to bounce the user through the OIDC redirect to an attacker
 * domain after the round-trip. While the `state` itself isn't
 * a credential, this opens the post-login tab on a phishing site
 * while the user is still authenticated.
 *
 * Shared by all three consoles: this SPA and, through `@shared`, the
 * support and admin consoles, both of which hand the result straight to
 * `window.location.assign`. So a hole here is a hole in all three.
 *
 * THE HOLE THIS CLOSES (twin slice 1, W4 step 2). The old check was a
 * string-prefix test: "starts with `/` and not with `//`". Browsers do not
 * read URLs as strings. In a special scheme the URL parser treats `\` as
 * `/`, and it deletes tab, CR and LF wherever they appear, so
 * `/\evil.com` and `/<TAB>/evil.com` both passed the prefix test and both
 * resolve to `https://evil.com/`. `/login?returnTo=%2F%5Cevil.com` was a
 * working open redirect.
 *
 * THE RULE NOW. Ask the URL parser itself, and refuse anything it would
 * have to repair:
 *
 * 1. the value must start with `/` -- a same-origin absolute path, never a
 *    relative path, a scheme or an absolute URL;
 * 2. it must contain no backslash and no C0 control or DEL character,
 *    anywhere. These are exactly the characters the parser rewrites or
 *    drops, so a value holding one means something other than it says;
 * 3. resolved with `new URL` against an origin of our own, it must still
 *    be on that origin (this is what catches `//evil.com`);
 * 4. its resolved path must not begin with `//`. Dot segments can collapse
 *    `/.//evil.com` or `/%2e%2e//evil.com` into `//evil.com` WITHOUT
 *    leaving the origin, and that string, handed to `location.assign` or
 *    `pushState`, is protocol-relative.
 *
 * What comes back is the parser's own serialisation of the path and
 * query, so it is exactly what the browser will load. The fragment is
 * dropped, as before, so it cannot collide with the OIDC `state`. A locate
 * link (`/overview?site=…&focus=robot:fleet/name`) passes through byte for
 * byte: its builder escapes everything the parser would.
 *
 * The fallback is `/overview` (a known-good route in the SPA).
 */

const FALLBACK = '/overview';

/**
 * The base the value is resolved against. Deliberately NOT the page's
 * own origin: an absolute URL naming this very origin is refused by rule
 * 1 anyway, so the answer cannot differ, and a fixed base keeps the check
 * identical in a browser, a test and an opaque-origin context where
 * `location.origin` is the string "null".
 */
const BASE = 'https://return-to.invalid';
const BASE_ORIGIN = new URL(BASE).origin;

/** Backslash, the C0 controls (U+0000–U+001F) and DEL (U+007F). */
// eslint-disable-next-line no-control-regex
const REWRITTEN_BY_THE_PARSER = /[\\\u0000-\u001f\u007f]/;

export function safeReturnTo(returnTo: string | undefined | null): string {
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/')) return FALLBACK;
  if (REWRITTEN_BY_THE_PARSER.test(returnTo)) return FALLBACK;
  let url: URL;
  try {
    url = new URL(returnTo, BASE);
  } catch {
    // Unreachable by the URL standard -- a path-absolute input against a
    // valid special base cannot fail -- and kept so that an engine which
    // disagrees fails closed rather than throwing out of the login page.
    return FALLBACK;
  }
  if (url.origin !== BASE_ORIGIN) return FALLBACK;
  const path = url.pathname + url.search;
  if (path.startsWith('//')) return FALLBACK;
  return path;
}
